import React, { createContext, useState, useEffect, useContext } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  useCheckStatusMutation,
  useGetAllQuery,
  useGetClerkAdminDataQuery,
} from "../api/api.slice";

const AuthContext = createContext({
  isAdmin: false,
  pendingTokenCount: 0,
});

export const AuthProvider = ({ children }) => {
  const { isSignedIn, user, isLoaded } = useUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingTokenCount, setPendingTokenCount] = useState(0);
  const [checkAdminStatus] = useCheckStatusMutation();

  const clerkId = user?.id;

  // STEP 1: Determine if user is admin (Clerk metadata OR backend)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !clerkId || !user) {
      setIsAdmin(false);
      return;
    }

    // Priority 1: If Clerk metadata says admin → trust it immediately
    if (user.publicMetadata?.role === "admin") {
      setIsAdmin(true);
      return;
    }

    // Priority 2: Otherwise check backend
    checkAdminStatus(clerkId)
      .unwrap()
      .then((res) => {
        setIsAdmin(!!res?.isAdmin);
      })
      .catch(() => {
        setIsAdmin(false); // 403 or error → not backend admin
      });
  }, [isLoaded, isSignedIn, clerkId, user, checkAdminStatus]);

  // STEP 2: Fetch data based on admin type
  const {
    data: backendData,
    isSuccess: backendSuccess,
    isError: backendError,
  } = useGetAllQuery(clerkId, {
    skip: !clerkId || !isAdmin, // only if admin
  });

  const { data: clerkAdminData, isSuccess: clerkSuccess } =
    useGetClerkAdminDataQuery(clerkId, {
      skip: !clerkId || !isAdmin,
    });

  // STEP 3: Compute pending count from correct data source
  useEffect(() => {
    if (!isAdmin) {
      setPendingTokenCount(0);
      return;
    }

    let applications = [];

    // If Clerk metadata admin → use clerkAdminData (this bypasses 403)
    if (user?.publicMetadata?.role === "admin" && clerkAdminData?.data) {
      applications = clerkAdminData.data;
    }
    // If backend admin → use normal Alldata (this works when backend allows)
    else if (backendSuccess && backendData?.data) {
      applications = backendData.data;
    }

    const pending = applications.filter(
      (app) => !app.verificationToken || app.verificationToken.trim() === ""
    ).length;

    setPendingTokenCount(pending);
  }, [isAdmin, backendData, backendSuccess, clerkAdminData, user]);

  return (
    <AuthContext.Provider value={{ isAdmin, pendingTokenCount }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
